import { setSelectedFarm } from './state';
import { can } from './access';
import { renderFarms, renderFarmDevices, farmNameById } from '../farms/farms-view';
import { renderFarmSettings } from '../farms/farm-settings-view';
import { renderClickerFarmDevices } from '../clicker/clicker-view';
import { renderClickerSettings } from '../clicker/clicker-settings-view';
import { renderFlows } from '../flows/flows-view';
import { renderFlowEditor, renderOperationRunPage } from '../flows/flow-editor-view';
import { renderUsersPage } from '../users/users';
import { renderUserForm } from '../users/user-form';
import { renderTeamForm } from '../users/team-form-view';
import { renderAdbProxy, renderAdbProxyDevices } from '../adb-proxy/adb-proxy-view';
import { renderWorkspace } from '../workspace/workspace-view';
import { renderWorkspaceConfig } from '../workspace/workspace-config-view';
import { renderDashboards } from '../dashboards/dashboards-view';
import { renderCharts } from '../charts/charts-view';
export function routeToHash(route) {
    if (route.page === 'workspace') {
        return route.config ? '#!/my/config' : '#!/my';
    }
    if (route.page === 'dashboards') {
        return route.tab && route.tab !== 'operations' ? `#!/dashboards/${route.tab}` : '#!/dashboards';
    }
    if (route.page === 'charts')
        return '#!/charts';
    if (route.page === 'adbProxy') {
        return route.farmId != null ? `#!/adb/${route.farmId}` : '#!/adb';
    }
    if (route.page === 'users') {
        if (route.teamForm === 'new')
            return '#!/teams/new';
        if (route.teamId)
            return `#!/teams/${route.teamId}/edit`;
        if (route.userForm === 'new')
            return '#!/users/new';
        if (route.userId)
            return `#!/users/${route.userId}/edit`;
        if (route.tab && route.tab !== 'users')
            return `#!/users/${route.tab}`;
        return '#!/users';
    }
    if (route.page === 'flows') {
        if (route.opId != null)
            return `#!/flows/op/${route.opId}`;
        return route.flowId != null ? `#!/flows/${route.flowId}` : '#!/flows';
    }
    if (route.page === 'clicker') {
        if (route.farmId != null)
            return `#!/clicker/farm/${route.farmId}`;
        return '#!/clicker';
    }
    if (route.farmId != null) {
        const base = `#!/farms/${route.farmId}`;
        return route.settings ? `${base}/settings` : base;
    }
    return '#!/farms';
}
export function parseRouteFromHash() {
    const raw = location.hash.replace(/^#!?\/?/, '').trim();
    // My Devices is the home page: opening the panel lands on the operator's own board
    // rather than the farm list. Every role can see it (UI_ACCESS 'page.workspace' =
    // operator), so this fallback is never a dead end.
    if (!raw) {
        return { page: 'workspace' };
    }
    const parts = raw.split('/').filter(Boolean);
    const section = parts[0];
    if (section === 'my') {
        return parts[1] === 'config' ? { page: 'workspace', config: true } : { page: 'workspace' };
    }
    if (section === 'dashboards') {
        if (parts[1] === 'devices')
            return { page: 'dashboards', tab: 'devices' };
        return parts[1] === 'operations' ? { page: 'dashboards', tab: 'operations' } : { page: 'dashboards' };
    }
    if (section === 'charts')
        return { page: 'charts' };
    if (section === 'adb') {
        const id = parts[1] ? Number.parseInt(parts[1], 10) : NaN;
        return Number.isInteger(id) ? { page: 'adbProxy', farmId: id } : { page: 'adbProxy' };
    }
    if (section === 'users') {
        if (parts[1] === 'new')
            return { page: 'users', userForm: 'new' };
        // Tab anchors — distinct from 'new' and from a numeric user id, so no collision.
        if (parts[1] === 'teams' || parts[1] === 'api')
            return { page: 'users', tab: parts[1] };
        const id = parts[1] ? Number.parseInt(parts[1], 10) : NaN;
        if (Number.isInteger(id) && parts[2] === 'edit')
            return { page: 'users', userId: id };
        return { page: 'users', tab: 'users' };
    }
    // Team add/edit pages live under the Users page (same nav item).
    if (section === 'teams') {
        if (parts[1] === 'new')
            return { page: 'users', teamForm: 'new' };
        const id = parts[1] ? Number.parseInt(parts[1], 10) : NaN;
        if (Number.isInteger(id) && parts[2] === 'edit')
            return { page: 'users', teamId: id };
        return { page: 'users' };
    }
    if (section === 'flows') {
        if (parts[1] === 'op') {
            const opId = parts[2] ? Number.parseInt(parts[2], 10) : NaN;
            return Number.isInteger(opId) ? { page: 'flows', opId } : { page: 'flows' };
        }
        const id = parts[1] ? Number.parseInt(parts[1], 10) : NaN;
        return Number.isInteger(id) ? { page: 'flows', flowId: id } : { page: 'flows' };
    }
    if (section === 'clicker') {
        if (parts[1] === 'farm' && parts[2]) {
            const id = Number.parseInt(parts[2], 10);
            if (Number.isInteger(id))
                return { page: 'clicker', farmId: id };
        }
        return { page: 'clicker' };
    }
    if (section === 'farms') {
        const id = parts[1] ? Number.parseInt(parts[1], 10) : NaN;
        if (!Number.isInteger(id)) {
            return { page: 'farms' };
        }
        return parts[2] === 'settings'
            ? { page: 'farms', farmId: id, settings: true }
            : { page: 'farms', farmId: id };
    }
    return { page: 'workspace' }; // unknown anchor → home
}
function setActiveNav(page) {
    const workspaceItem = document.getElementById('nav-workspace');
    workspaceItem?.classList.toggle('active', page === 'workspace');
    document.getElementById('nav-dashboards')?.classList.toggle('active', page === 'dashboards');
    document.getElementById('nav-charts')?.classList.toggle('active', page === 'charts');
    const farmsItem = document.getElementById('nav-farms');
    const clickerItem = document.getElementById('nav-clicker');
    const usersItem = document.getElementById('nav-users');
    const adbItem = document.getElementById('nav-adb');
    farmsItem?.classList.toggle('active', page === 'farms');
    clickerItem?.classList.toggle('active', page === 'clicker');
    document.getElementById('nav-flows')?.classList.toggle('active', page === 'flows');
    usersItem?.classList.toggle('active', page === 'users');
    adbItem?.classList.toggle('active', page === 'adbProxy');
}
export function renderRoute(route) {
    if (route.page === 'workspace') {
        setSelectedFarm(null);
        setActiveNav('workspace');
        if (route.config)
            void renderWorkspaceConfig();
        else
            void renderWorkspace();
        return;
    }
    if (route.page === 'dashboards') {
        // Every authenticated user; the guard stays so a page removed from the sidebar
        // (or a stale deep link) still lands on the user's own board.
        if (!can('page.dashboards') || !document.getElementById('nav-dashboards')) {
            setSelectedFarm(null);
            setActiveNav('workspace');
            if (location.hash !== routeToHash({ page: 'workspace' })) {
                history.replaceState(null, '', routeToHash({ page: 'workspace' }));
            }
            void renderWorkspace();
            return;
        }
        setSelectedFarm(null);
        setActiveNav('dashboards');
        renderDashboards(route.tab);
        return;
    }
    if (route.page === 'charts') {
        // Admin+; a lower role deep-linking here lands on their own board.
        if (!can('page.charts') || !document.getElementById('nav-charts')) {
            setSelectedFarm(null);
            setActiveNav('workspace');
            if (location.hash !== routeToHash({ page: 'workspace' })) {
                history.replaceState(null, '', routeToHash({ page: 'workspace' }));
            }
            void renderWorkspace();
            return;
        }
        setSelectedFarm(null);
        setActiveNav('charts');
        renderCharts();
        return;
    }
    if (route.page === 'adbProxy') {
        // Superadmin-only page; a lower role deep-linking to #!/adb falls back to farms.
        if (!can('page.adbProxy') || !document.getElementById('nav-adb')) {
            setSelectedFarm(null);
            setActiveNav('farms');
            if (location.hash !== routeToHash({ page: 'farms' })) {
                history.replaceState(null, '', routeToHash({ page: 'farms' }));
            }
            renderFarms();
            return;
        }
        setSelectedFarm(null);
        setActiveNav('adbProxy');
        if (route.farmId != null) {
            void renderAdbProxyDevices(route.farmId);
        }
        else {
            void renderAdbProxy();
        }
        return;
    }
    if (route.page === 'flows') {
        // Two pages behind one route. `#!/flows/op/<id>` is one stored run, read-only, and is
        // open to admins — they have no sidebar item for it and reach it from the registry's
        // trajectory button, so it must NOT require nav-flows. Everything else here is the
        // authoring section: superadmin, and a lower role deep-linking to it lands on their board.
        const runPage = route.opId != null;
        if (runPage ? !can('page.flowRun')
            : (!can('page.flows') || !document.getElementById('nav-flows'))) {
            setSelectedFarm(null);
            setActiveNav('workspace');
            if (location.hash !== routeToHash({ page: 'workspace' })) {
                history.replaceState(null, '', routeToHash({ page: 'workspace' }));
            }
            void renderWorkspace();
            return;
        }
        setSelectedFarm(null);
        setActiveNav('flows');
        if (route.opId != null)
            void renderOperationRunPage(route.opId);
        else if (route.flowId != null)
            void renderFlowEditor(route.flowId);
        else
            void renderFlows();
        return;
    }
    if (route.page === 'clicker') {
        // Superadmin only; anyone else deep-linking to #!/clicker lands on their own
        // board instead.
        if (!can('page.clicker') || !document.getElementById('nav-clicker')) {
            setSelectedFarm(null);
            setActiveNav('workspace');
            if (location.hash !== routeToHash({ page: 'workspace' })) {
                history.replaceState(null, '', routeToHash({ page: 'workspace' }));
            }
            void renderWorkspace();
            return;
        }
        setSelectedFarm(route.farmId != null ? (farmNameById(route.farmId) ?? null) : null, route.farmId ?? null);
        setActiveNav('clicker');
        if (route.farmId != null)
            void renderClickerFarmDevices(route.farmId);
        else
            void renderClickerSettings();
        return;
    }
    if (route.page === 'users') {
        if (!can('page.users') || !document.getElementById('nav-users')) {
            setSelectedFarm(null);
            setActiveNav('farms');
            if (location.hash !== routeToHash({ page: 'farms' })) {
                history.replaceState(null, '', routeToHash({ page: 'farms' }));
            }
            renderFarms();
            return;
        }
        setSelectedFarm(null);
        setActiveNav('users');
        if (route.teamForm === 'new') {
            void renderTeamForm('new');
        }
        else if (route.teamId) {
            void renderTeamForm('edit', route.teamId);
        }
        else if (route.userForm === 'new') {
            void renderUserForm('new');
        }
        else if (route.userId) {
            void renderUserForm('edit', route.userId);
        }
        else {
            void renderUsersPage(route.tab);
        }
        return;
    }
    setSelectedFarm(route.farmId != null ? (farmNameById(route.farmId) ?? null) : null, route.farmId ?? null);
    setActiveNav('farms');
    if (route.farmId != null) {
        if (route.settings && can('farm.deviceAccess')) {
            void renderFarmSettings(route.farmId);
        }
        else {
            renderFarmDevices();
        }
    }
    else {
        renderFarms();
    }
}
export function navigate(route) {
    const hash = routeToHash(route);
    if (location.hash === hash) {
        renderRoute(route);
        return;
    }
    location.hash = hash;
}
export function syncRouteToHash() {
    const route = parseRouteFromHash();
    const hash = routeToHash(route);
    if (location.hash !== hash) {
        history.replaceState(null, '', hash);
    }
}
let routeListenerInstalled = false;
export function installRouteListener() {
    if (routeListenerInstalled) {
        return;
    }
    routeListenerInstalled = true;
    window.addEventListener('hashchange', () => {
        renderRoute(parseRouteFromHash());
    });
}
